import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Phone, Mail, Lock, ArrowRight, Sparkles, Shield } from "lucide-react";
import { toast } from "sonner";
import { sanitizePhone, isValidPhone, PHONE_INPUT_PROPS } from "@/lib/phone";

export const Route = createFileRoute("/login")({
  head: () => ({ meta: [{ title: "Login — Jain Gaurav Yatra" }] }),
  component: LoginPage,
});

function LoginPage() {
  const nav = useNavigate();
  const [authMode, setAuthMode] = useState<"signin" | "signup">("signin");
  const [mode, setMode] = useState<"phone" | "email">("phone");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "phone" && !isValidPhone(phone)) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    // Demo mode — any ID/password is accepted
    toast.success(authMode === "signup" ? "Account created · Welcome!" : "Welcome back!");
    nav({ to: "/app" });
  };

  return (
    <div
      className="flex min-h-screen flex-col md:flex-row"
      style={{ background: "var(--gradient-warm)" }}
    >
      {/* Left brand panel */}
      <div
        className="relative hidden flex-1 flex-col justify-between overflow-hidden p-10 text-primary-foreground md:flex"
        style={{ background: "var(--gradient-sunset)" }}
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{ background: "var(--gradient-mesh)" }}
        />
        <div className="relative">
          <div className="flex items-center gap-2">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur">
              <Sparkles className="h-5 w-5" />
            </span>
            <span className="font-serif text-xl font-bold">Jain Gaurav Yatra</span>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-serif text-4xl font-bold leading-tight">
            Sacred journey,
            <br />
            simplified.
          </h1>
          <p className="mt-3 max-w-sm text-sm opacity-90">
            Plan visits, book rickshaws & guides, and earn your Yatra completion certificate — all
            in one app.
          </p>
          <div className="mt-6 flex gap-2">
            <span className="rounded-full bg-white/20 px-3 py-1 text-[11px] backdrop-blur">
              102 Temples
            </span>
            <span className="rounded-full bg-white/20 px-3 py-1 text-[11px] backdrop-blur">
              Multi-language
            </span>
            <span className="rounded-full bg-white/20 px-3 py-1 text-[11px] backdrop-blur">
              Group Visits
            </span>
          </div>
        </div>
        <div className="relative text-[11px] opacity-70">© Jain Gaurav Yatra Trust</div>
      </div>

      {/* Right form */}
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="mb-6 md:hidden">
            <div className="flex items-center gap-2">
              <span
                className="flex h-9 w-9 items-center justify-center rounded-xl text-primary-foreground"
                style={{ background: "var(--gradient-saffron)" }}
              >
                <Sparkles className="h-4 w-4" />
              </span>
              <span className="font-serif text-lg font-bold">Jain Gaurav Yatra</span>
            </div>
          </div>

          <div className="rounded-3xl border border-border bg-card p-7 shadow-xl">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Devotee {authMode === "signup" ? "Sign Up" : "Login"}
            </div>
            <h2 className="font-serif text-2xl font-bold">Jai Jinendra 🙏</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {authMode === "signup"
                ? "Create your account to begin."
                : "Sign in to continue your sacred journey."}
            </p>

            <div className="mt-4 grid grid-cols-2 gap-1 rounded-full border border-border bg-muted p-1">
              {(["signin", "signup"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setAuthMode(m)}
                  className={`rounded-full py-1.5 text-xs font-semibold transition ${authMode === m ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}
                >
                  {m === "signin" ? "Sign In" : "Sign Up"}
                </button>
              ))}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-1 rounded-full border border-border bg-muted p-1">
              {(["phone", "email"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`rounded-full py-1.5 text-xs font-semibold capitalize transition ${mode === m ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}
                >
                  {m}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="mt-5 space-y-3">
              {authMode === "signup" && (
                <InputBlock icon={Sparkles} label="Full Name">
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full bg-transparent text-sm outline-none"
                  />
                </InputBlock>
              )}
              {mode === "phone" ? (
                <InputBlock icon={Phone} label="Mobile Number">
                  <input
                    {...PHONE_INPUT_PROPS}
                    required
                    value={phone}
                    onChange={(e) => setPhone(sanitizePhone(e.target.value))}
                    className="w-full bg-transparent text-sm outline-none"
                  />
                </InputBlock>
              ) : (
                <>
                  <InputBlock icon={Mail} label="Email">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full bg-transparent text-sm outline-none"
                    />
                  </InputBlock>
                  <InputBlock icon={Lock} label="Password">
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full bg-transparent text-sm outline-none"
                    />
                  </InputBlock>
                </>
              )}
              <button type="submit" className="btn-primary mt-2 w-full py-3 text-sm font-bold">
                {authMode === "signup"
                  ? "Create Account"
                  : mode === "phone"
                    ? "Send OTP"
                    : "Sign In"}{" "}
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>

            <div className="mt-3 text-center text-[11px] text-muted-foreground">
              {authMode === "signin" ? (
                <>
                  New devotee?{" "}
                  <button
                    onClick={() => setAuthMode("signup")}
                    className="font-semibold text-primary hover:underline"
                  >
                    Create account
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <button
                    onClick={() => setAuthMode("signin")}
                    className="font-semibold text-primary hover:underline"
                  >
                    Sign in
                  </button>
                </>
              )}
            </div>

            <Link
              to="/app"
              className="mt-2 block text-center text-xs text-muted-foreground hover:text-primary"
            >
              Continue as guest →
            </Link>
          </div>

          <Link
            to="/admin/login"
            className="mt-4 flex items-center justify-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary"
          >
            <Shield className="h-3.5 w-3.5" /> Admin Portal Login
          </Link>
        </div>
      </div>
    </div>
  );
}

function InputBlock({
  icon: Icon,
  label,
  children,
}: {
  icon: any;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-background px-3 py-2 transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
